import { getLovLabel } from '@zoryn/hooks'
import { ZMessage } from '@zoryn/components/message'
import {
  getOperator,
  resolveDefaultFilterOperator,
  resolveFilterColumnType,
} from '../filter/filter-meta'
import {
  createFilterId,
  findFirstFieldCondition,
  isActiveColumnMenuFilter,
  removeFieldFromFilterGroups,
  sanitizeFilterGroups,
  upsertFieldConditionInFirstGroup,
} from '../filter/filter-group'
import {
  COLUMN_MENU_VALUE_LIST_LIMIT,
  fetchDistinctColumnValues,
} from '../utils/column-distinct-query'
import { formatDateDisplayText, isDateColumnType } from '../utils/date-cell'
import type {
  DataGrid,
  FilterColumnType,
  GridColumnInfo,
  GridColumnMenuStore,
  GridFilter,
} from '../types'

function createColumnFilterDraft(
  column: GridColumnInfo
): Pick<
  GridColumnMenuStore,
  'filterOperator' | 'filterValue' | 'filterType' | 'operatorList'
> {
  const filterType = resolveFilterColumnType(column.type ?? 'base')
  return {
    filterType,
    filterOperator: resolveDefaultFilterOperator(filterType, column),
    filterValue: null,
    operatorList: getOperator(filterType),
  }
}

function createColumnFilter(
  column: GridColumnInfo,
  store: GridColumnMenuStore
): GridFilter {
  return {
    id: createFilterId('cond'),
    field: column.field!,
    operator: store.filterOperator,
    type: store.filterType,
    value: store.filterValue,
    logic: 'and',
    operatorList: store.operatorList,
  }
}

function limitColumnMenuValueOptions<
  T extends { value: unknown; label: string },
>(options: T[]): T[] {
  return options.slice(0, COLUMN_MENU_VALUE_LIST_LIMIT)
}

function getMenuColumn($grid: DataGrid): GridColumnInfo | null {
  const { columnMenuStore } = $grid.reactData
  if (!columnMenuStore.columnId) {
    return null
  }
  return (
    $grid.internalData.allColumns.find(
      (col) => col.id === columnMenuStore.columnId
    ) ?? null
  )
}

function getColumnValues($grid: DataGrid, column: GridColumnInfo) {
  const field = column.field
  if (!field) {
    return []
  }
  return $grid.reactData.tableData
    .map((row) => row[field])
    .filter((val) => val !== null && val !== undefined && val !== '')
}

function getUniqueValues($grid: DataGrid, column: GridColumnInfo) {
  const field = column.field
  if (!field) {
    return []
  }
  const set = new Set<unknown>()
  for (const row of $grid.reactData.tableData) {
    const val = row[field]
    if (val !== null && val !== undefined && val !== '') {
      set.add(val)
    }
  }
  return [...set]
}

function hasDistinctOverlapWithLocal(
  serverOptions: Array<{ value: unknown; label: string }>,
  localOptions: Array<{ value: unknown; label: string }>
) {
  if (!serverOptions.length || !localOptions.length) {
    return true
  }
  const localSet = new Set(localOptions.map((item) => String(item.value)))
  return serverOptions.some((item) => localSet.has(String(item.value)))
}

/** 服务端 distinct 失败时常见：返回 LIMIT 条主键 1..n */
export function isLikelyRowIdDistinct(
  options: Array<{ value: unknown; label: string }>
) {
  if (options.length < 2) {
    return false
  }
  return options.every((item, index) => {
    const num = Number(item.value)
    return (
      Number.isInteger(num) &&
      num === index + 1 &&
      String(item.label) === String(item.value)
    )
  })
}

function resolveDistinctValueOptions(
  column: GridColumnInfo,
  serverOptions: Array<{ value: unknown; label: string }>,
  localOptions: Array<{ value: unknown; label: string }>
) {
  if (!serverOptions.length) {
    return localOptions
  }
  const normalized = normalizeDistinctColumnOptions(column, serverOptions)
  if (
    isLikelyRowIdDistinct(normalized) ||
    (localOptions.length &&
      !hasDistinctOverlapWithLocal(normalized, localOptions))
  ) {
    return localOptions
  }
  return limitColumnMenuValueOptions(normalized)
}

function formatColumnValueLabel(
  column: GridColumnInfo,
  value: unknown
): string {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  if (column.formatter) {
    return column.formatter({
      row: { [column.field!]: value },
      column,
      cellValue: value,
    })
  }
  if (column.type === 'lov' && column.lovType) {
    return getLovLabel(column.lovType, value) || String(value)
  }
  if (isDateColumnType(column.type)) {
    return formatDateDisplayText(value, column.type) || String(value)
  }
  return String(value)
}

function normalizeDistinctColumnOptions(
  column: GridColumnInfo,
  options: Array<{ value: unknown; label: string }>
) {
  return options.map((item) => ({
    value: item.value,
    label:
      formatColumnValueLabel(column, item.value) ||
      item.label ||
      String(item.value ?? ''),
  }))
}

function resolveMenuFilterDraft(
  $grid: DataGrid,
  column: GridColumnInfo
): Pick<
  GridColumnMenuStore,
  'filterOperator' | 'filterValue' | 'filterType' | 'operatorList'
> {
  const existing = findFirstFieldCondition(
    $grid.reactData.filterGroups,
    column.field!
  )
  if (existing) {
    return {
      filterOperator: existing.operator,
      filterValue: existing.value,
      filterType: existing.type as FilterColumnType,
      operatorList: existing.operatorList,
    }
  }
  return createColumnFilterDraft(column)
}

function hasActiveMenuFilter(store: GridColumnMenuStore) {
  return isActiveColumnMenuFilter(store.filterOperator, store.filterValue)
}

export function createColumnMenuStore(): GridColumnMenuStore {
  const filterType: FilterColumnType = 'input'
  return {
    visible: false,
    columnId: null,
    referenceEl: null,
    sortOrder: null,
    filterOperator: 'like',
    filterValue: null,
    filterType,
    operatorList: getOperator(filterType),
    valueOptions: [],
    valueOptionsLoading: false,
    valueKeyword: '',
  }
}

export function useColumnMenu($grid: DataGrid) {
  const { reactData, internalData } = $grid

  function closeColumnMenu() {
    reactData.columnMenuStore.visible = false
    reactData.columnMenuStore.referenceEl = null
  }

  function filterLocalDistinctOptions(
    column: GridColumnInfo,
    keyword?: string
  ) {
    const options = getLocalDistinctOptions(column)
    const text = keyword?.trim()
    if (!text) {
      return limitColumnMenuValueOptions(options)
    }
    const lower = text.toLowerCase()
    return limitColumnMenuValueOptions(
      options.filter(
        (item) =>
          String(item.label).toLowerCase().includes(lower) ||
          String(item.value).toLowerCase().includes(lower)
      )
    )
  }

  function loadColumnDistinctValues(column: GridColumnInfo, keyword?: string) {
    const store = reactData.columnMenuStore
    const searchText = keyword ?? store.valueKeyword
    store.valueOptionsLoading = true
    void fetchDistinctColumnValues($grid, column, searchText)
      .then((options) => {
        if (store.columnId !== column.id) {
          return
        }
        const localOptions = filterLocalDistinctOptions(column, searchText)
        store.valueOptions = resolveDistinctValueOptions(
          column,
          options,
          localOptions
        )
      })
      .catch(() => {
        if (store.columnId !== column.id) {
          return
        }
        store.valueOptions = filterLocalDistinctOptions(column, searchText)
      })
      .finally(() => {
        if (store.columnId === column.id) {
          store.valueOptionsLoading = false
        }
      })
  }

  function getLocalDistinctOptions(column: GridColumnInfo) {
    return limitColumnMenuValueOptions(
      getUniqueValues($grid, column).map((value) => ({
        value,
        label: formatColumnValueLabel(column, value) || String(value),
      }))
    )
  }

  $grid.openColumnMenu = (column, referenceEl) => {
    if (column.type === 'checkbox' || column.type === 'seq' || !column.field) {
      return
    }
    const filterDraft = resolveMenuFilterDraft($grid, column)
    Object.assign(reactData.columnMenuStore, {
      visible: true,
      columnId: column.id,
      referenceEl,
      sortOrder: column.order ?? null,
      valueOptions: [],
      valueOptionsLoading: true,
      valueKeyword: '',
      ...filterDraft,
    })
    reactData.initStore.columnMenu = true
    loadColumnDistinctValues(column)
  }

  $grid.reloadColumnDistinctValues = () => {
    const column = getMenuColumn($grid)
    if (!column?.field) {
      return
    }
    loadColumnDistinctValues(column)
  }

  $grid.closeColumnMenu = closeColumnMenu

  $grid.restoreColumnMenu = () => {
    const column = getMenuColumn($grid)
    if (!column?.field) {
      return
    }
    Object.assign(reactData.columnMenuStore, {
      sortOrder: null,
      ...createColumnFilterDraft(column),
    })
  }

  $grid.applyColumnMenu = () => {
    const column = getMenuColumn($grid)
    const store = reactData.columnMenuStore
    if (!column?.field) {
      return
    }

    if (column.sortable) {
      if (store.sortOrder === 'asc' || store.sortOrder === 'desc') {
        column.order = store.sortOrder
        internalData.sortMap[column.field] = column
      } else {
        column.order = null
        delete internalData.sortMap[column.field]
      }
    }

    if (hasActiveMenuFilter(store)) {
      upsertFieldConditionInFirstGroup(
        reactData.filterGroups,
        column,
        createColumnFilter(column, store)
      )
    } else {
      reactData.filterGroups = removeFieldFromFilterGroups(
        reactData.filterGroups,
        column.field!
      )
    }

    reactData.filterGroups = sanitizeFilterGroups(reactData.filterGroups)

    reactData.currentPage = 1
    void $grid.queryByExamplePage()
    closeColumnMenu()
  }

  $grid.clearColumnMenuFilter = () => {
    const column = getMenuColumn($grid)
    if (!column?.field) {
      return
    }
    Object.assign(reactData.columnMenuStore, createColumnFilterDraft(column))
    reactData.filterGroups = removeFieldFromFilterGroups(
      reactData.filterGroups,
      column.field!
    )
    reactData.currentPage = 1
    void $grid.queryByExamplePage()
  }

  $grid.getColumnUniqueCount = (column: GridColumnInfo) => {
    return getUniqueValues($grid, column).length
  }

  $grid.copyColumnValues = async (column: GridColumnInfo) => {
    const field = column.field
    if (!field) {
      return
    }
    const text = getColumnValues($grid, column)
      .map((val) => String(val))
      .join('\n')
    if (!text) {
      ZMessage.warning('当前列无数据可复制')
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      ZMessage.success('已复制当前列数据')
    } catch {
      ZMessage.error('复制失败')
    }
  }

  $grid.showColumnStatistics = (column: GridColumnInfo) => {
    const field = column.field
    if (!field) {
      return
    }
    const values = getColumnValues($grid, column)
    const uniqueCount = new Set(values).size
    if (column.type === 'number') {
      const numbers = values.map(Number).filter((val) => !Number.isNaN(val))
      if (!numbers.length) {
        ZMessage.info('当前列无有效数值')
        return
      }
      const sum = numbers.reduce((acc, val) => acc + val, 0)
      const avg = sum / numbers.length
      const min = Math.min(...numbers)
      const max = Math.max(...numbers)
      ZMessage.info(
        `计数 ${numbers.length}，唯一 ${uniqueCount}，合计 ${sum}，均值 ${avg.toFixed(2)}，最小 ${min}，最大 ${max}`
      )
      return
    }
    ZMessage.info(`计数 ${values.length}，唯一值 ${uniqueCount}`)
  }

  $grid.getColumnUniqueOptions = (column: GridColumnInfo) => {
    const { columnMenuStore } = reactData
    if (columnMenuStore.columnId === column.id) {
      return columnMenuStore.valueOptions
    }
    return getLocalDistinctOptions(column)
  }

  $grid.toggleColumnFixed = () => {
    const column = getMenuColumn($grid)
    if (!column) {
      return
    }
    if (column.fixed === 'left') {
      column.fixed = 'right'
    } else if (column.fixed === 'right') {
      column.fixed = ''
    } else {
      column.fixed = 'left'
    }
  }
}
